import { sqWorkbenchStore, sqWorksheetStore } from '@/core/core.stores';
import { AgGridModules } from '@/core/tableUtilities/tableUtilities.types';
import { isSystemTest } from '@/core/utilities';
import { TIME_FORMAT_NUMERICAL_LONG, formatTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import type { AgGridAsyncProps } from '@/core/tableUtilities/tableUtilities.types';
import type { AnyProperty } from '@/utilities.types';
import type { ColDef, DataTypeDefinition } from '@seeqdev/ag/grid';

export const COMMON_AG_GRID_MODULES = [
  AgGridModules.SetFilterModule,
  AgGridModules.ColumnMenuModule,
  AgGridModules.ContextMenuModule,
  AgGridModules.ColumnsToolPanelModule,
  AgGridModules.RowAutoHeightModule,
  AgGridModules.RowStyleModule,
  AgGridModules.PaginationModule,
  AgGridModules.RowDragModule,
  AgGridModules.RowNumbersModule,
  AgGridModules.PinnedRowModule,
  AgGridModules.RowSelectionModule,
  AgGridModules.RowApiModule,
  AgGridModules.TextFilterModule,
  AgGridModules.DateFilterModule,
  AgGridModules.NumberFilterModule,
  AgGridModules.MultiFilterModule,
  AgGridModules.LocaleModule,
  AgGridModules.TooltipModule,
  AgGridModules.ColumnAutoSizeModule,
];

export const DEFAULT_ROW_SELECTION = {
  mode: 'multiRow',
  enableClickSelection: true,
  checkboxes: true,
  selectAll: 'filtered',
} as const;

export const DEFAULT_COL_DEF: ColDef = {
  filter: 'agMultiColumnFilter',
  filterParams: {
    filters: [
      {
        filter: 'agTextColumnFilter',
      },
      {
        filter: 'agSetColumnFilter',
      },
    ],
  },
  floatingFilter: true,
};

export const STATIC_COL_DEF: ColDef = {
  headerName: '',
  suppressHeaderMenuButton: true,
  suppressMovable: true,
  suppressAutoSize: true,
  suppressFiltersToolPanel: true,
  suppressColumnsToolPanel: true,
  sortable: false,
  resizable: false,
  width: 50,
  floatingFilter: false,
  filter: false,
};

export const CUSTOM_TYPE_DEFINITIONS: AnyProperty<DataTypeDefinition> = {
  dateTime: {
    baseDataType: 'date',
    extendsDataType: 'date',
    valueFormatter: ({ value }) => {
      if (!value) return '-';
      return formatTime(value, sqWorksheetStore.timezone, getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat));
    },
  },
  dateTimeWithSeconds: {
    baseDataType: 'date',
    extendsDataType: 'date',
    valueFormatter: ({ value }) => {
      if (!value) return '-';
      return formatTime(
        value,
        sqWorksheetStore.timezone,
        getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, TIME_FORMAT_NUMERICAL_LONG),
      );
    },
  },
};

/**
 * Suppress row virtualisation in test environments to avoid 0x0 grid elements not working with horizontal scrolling
 * (`ensureColumnVisible`, for example)
 */
export const shouldSuppressAgGridVirtualisation = (): boolean => isSystemTest() || process.env.NODE_ENV === 'test';

export const DEFAULT_AG_GRID_PROPS: AgGridAsyncProps = {
  modules: COMMON_AG_GRID_MODULES,
  accentedSort: true,
  tooltipShowDelay: 500,
  suppressColumnVirtualisation: shouldSuppressAgGridVirtualisation(),
  suppressRowVirtualisation: shouldSuppressAgGridVirtualisation(),
  defaultColDef: DEFAULT_COL_DEF,
  dataTypeDefinitions: CUSTOM_TYPE_DEFINITIONS,
  rowSelection: {
    mode: 'multiRow',
    enableClickSelection: true,
  },
};

/**
 * A subset of AG-Grid properties to include when you want to print the grid in a nicely formatted way
 */
export const PRINTING_AG_GRID_PROPS: Partial<AgGridAsyncProps> = {
  suppressRowVirtualisation: true,
  suppressColumnVirtualisation: true,
  animateRows: false,
  domLayout: 'print',
};

export const EXTRA_TOPIC_PROPS = { getChartToolbarItems: () => [] };

export const EXTRA_NON_TOPIC_PROPS = {};

export const DEFAULT_PAGINATION_SIZE_SELECTOR = [10, 15, 20, 25, 30, 50, 100];

export const MULTILINE_STYLE = { lineHeight: '1.7em' };
